// search-highlight.ts — Découpe pure d'un texte en segments surlignés selon les
// termes d'une requête. Sert à la palette de recherche pour mettre en évidence
// les correspondances SANS injecter de HTML serveur (chaque segment est passé
// par esc() côté page — cf. règle innerHTML/DT1). Insensible à la casse ET aux
// accents (« echeance » surligne « échéance »). Testé (search-highlight.test.ts).

export interface HighlightSegment {
  text: string;
  hit: boolean;
}

// Plage Unicode des marques diacritiques combinantes (accents décomposés en NFD).
const COMBINING_MARKS = /[̀-ͯ]/g;

// Replie un caractère unique (UTF-16) : minuscule + accents retirés, en
// GARANTISSANT une longueur de 1 pour préserver l'alignement d'index avec la
// chaîne source (sinon les offsets de surlignage seraient décalés). Fallback sur
// la minuscule brute pour les rares caractères qui ne se replient pas en 1 char.
function foldChar(ch: string): string {
  const f = ch.normalize('NFD').replace(COMBINING_MARKS, '').toLowerCase();
  return f.length === 1 ? f : ch.toLowerCase();
}

function foldStr(s: string): string {
  let out = '';
  for (let i = 0; i < s.length; i++) out += foldChar(s.charAt(i));
  return out;
}

// Termes signifiants (≥ 2 caractères, dédupliqués) d'une requête libre.
export function queryTerms(query: string): string[] {
  return Array.from(
    new Set(
      foldStr(query)
        .split(/[^a-z0-9]+/)
        .filter((tk) => tk.length >= 2),
    ),
  );
}

// Découpe `text` en segments consécutifs marqués `hit` s'ils sont couverts par
// un terme. Concaténer les `.text` redonne EXACTEMENT `text` (aucune perte).
export function highlightSegments(text: string, query: string): HighlightSegment[] {
  const src = text || '';
  if (!src) return [];
  const terms = queryTerms(query);
  if (terms.length === 0) return [{ text: src, hit: false }];
  const folded = foldStr(src);
  const covered: boolean[] = new Array(src.length).fill(false);
  for (const term of terms) {
    let from = 0;
    for (;;) {
      const idx = folded.indexOf(term, from);
      if (idx === -1) break;
      for (let i = idx; i < idx + term.length; i++) covered[i] = true;
      from = idx + term.length;
    }
  }
  const hitAt = (idx: number): boolean => covered[idx] ?? false;
  const out: HighlightSegment[] = [];
  let start = 0;
  for (let i = 1; i <= src.length; i++) {
    if (i === src.length || hitAt(i) !== hitAt(start)) {
      out.push({ text: src.slice(start, i), hit: hitAt(start) });
      start = i;
    }
  }
  return out;
}
